import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { authHeader, clearToken, isAuthed } from "@/lib/auth";
import { toast } from "sonner";
import {
  Mail, FileText, LogOut, Archive, ArchiveRestore, CircleDot, Circle,
  Phone, Building2, ImageIcon, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminInbox() {
  const nav = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [showArchived, setShowArchived] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({ quotes_unread: 0, quotes_total: 0, contacts_unread: 0, contacts_total: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isAuthed()) { nav("/admin"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const handle401 = (err) => {
    if (err?.response?.status === 401) { clearToken(); nav("/admin"); return true; }
    return false;
  };

  const load = async () => {
    try {
      const q = showArchived ? { archived: true } : { archived: false };
      const [qr, cr, counts] = await Promise.all([
        axios.get(`${API}/quote`, { headers: authHeader(), params: q }),
        axios.get(`${API}/contact`, { headers: authHeader(), params: q }),
        axios.get(`${API}/admin/inbox/counts`, { headers: authHeader() }),
      ]);
      setQuotes(qr.data); setContacts(cr.data); setCounts(counts.data);
    } catch (err) { if (!handle401(err)) toast.error("Load failed"); }
  };

  const items = tab === "quotes" ? quotes : contacts;
  const selected = items.find(i => i.id === selectedId);

  const markRead = async (id, read) => {
    try {
      const endpoint = tab === "quotes" ? "quote" : "contact";
      await axios.patch(`${API}/admin/${endpoint}/${id}`, { read }, { headers: authHeader() });
      await load();
    } catch (err) { if (!handle401(err)) toast.error("Update failed"); }
  };

  const toggleArchive = async (id, archived) => {
    try {
      const endpoint = tab === "quotes" ? "quote" : "contact";
      await axios.patch(`${API}/admin/${endpoint}/${id}`, { archived }, { headers: authHeader() });
      toast.success(archived ? "Archived" : "Restored");
      setSelectedId(null);
      await load();
    } catch (err) { if (!handle401(err)) toast.error("Update failed"); }
  };

  const openItem = (item) => {
    setSelectedId(item.id);
    if (!item.read_at) markRead(item.id, true);
  };

  const sendTestEmail = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-email`, {}, { headers: authHeader() });
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      if (!handle401(err)) toast.error(err?.response?.data?.detail || "Test email failed — is RESEND_API_KEY set?");
    } finally { setSending(false); }
  };

  const signOut = () => { clearToken(); nav("/admin"); };

  return (
    <div className="section px-6 lg:px-10" data-testid="admin-inbox-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-3">Admin · Inbox</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Leads &amp; Messages</h1>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <Link to="/admin/gallery" className="text-sm text-zinc-400 hover:text-[#FF5722] underline decoration-white/20">→ Gallery</Link>
            <span className="text-zinc-600 mx-1">·</span>
            <button onClick={sendTestEmail} disabled={sending} className="text-sm text-zinc-400 hover:text-[#FF5722] underline decoration-white/20" data-testid="test-email-btn">
              <Send className="w-3 h-3 inline mr-1" /> {sending ? "Sending..." : "Send test email"}
            </button>
          </div>
        </div>
        <button onClick={signOut} className="btn-ghost" data-testid="inbox-signout"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <TabBtn active={tab === "quotes"} onClick={() => { setTab("quotes"); setSelectedId(null); }} icon={FileText}
          label="Quotes" count={counts.quotes_total} unread={counts.quotes_unread} testId="tab-quotes" />
        <TabBtn active={tab === "contacts"} onClick={() => { setTab("contacts"); setSelectedId(null); }} icon={Mail}
          label="Contacts" count={counts.contacts_total} unread={counts.contacts_unread} testId="tab-contacts" />
        <button onClick={() => { setShowArchived(!showArchived); setSelectedId(null); }}
          className={`ml-auto px-3 py-2 border font-display uppercase tracking-widest text-xs transition-colors ${showArchived ? "bg-white/10 border-white/20" : "border-white/10 hover:border-white/30"}`}
          data-testid="toggle-archived">
          {showArchived ? "← Active" : "Archived →"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LIST */}
        <div className="lg:col-span-5 border border-white/10 bg-[#0B0B0D] max-h-[75vh] overflow-y-auto" data-testid="inbox-list">
          {items.length === 0 ? (
            <div className="p-12 text-center text-zinc-500 text-sm">{showArchived ? "No archived items." : "Inbox zero. 🎯"}</div>
          ) : items.map((it) => (
            <button
              key={it.id}
              onClick={() => openItem(it)}
              className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors ${selectedId === it.id ? "bg-white/[0.04]" : ""} ${!it.read_at ? "border-l-2 border-l-[#FF5722]" : "border-l-2 border-l-transparent"}`}
              data-testid={`inbox-item-${it.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg tracking-tight truncate">
                    {tab === "quotes" ? it.company : it.name}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {tab === "quotes" ? `${it.contact_name} · ${it.service}` : (it.subject || "(no subject)")}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{fmt(it.created_at)}</div>
              </div>
              <div className="text-xs text-zinc-400 mt-2 line-clamp-2">
                {(it.message || "").slice(0, 120)}{(it.message || "").length > 120 ? "…" : ""}
              </div>
              {!it.read_at && !it.archived && <span className="mt-2 inline-block text-[10px] uppercase tracking-widest text-[#FF5722]">● New</span>}
            </button>
          ))}
        </div>

        {/* DETAIL */}
        <div className="lg:col-span-7 border border-white/10 bg-[#0B0B0D] p-6 min-h-[400px]" data-testid="inbox-detail">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Select a message to view details.</div>
          ) : (
            <div>
              <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                  <div className="eyebrow mb-2">{tab === "quotes" ? "Quote Request" : "Contact Message"} · {fmt(selected.created_at)}</div>
                  <h2 className="font-display text-3xl tracking-tighter">
                    {tab === "quotes" ? selected.company : selected.name}
                  </h2>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => markRead(selected.id, !selected.read_at)} className="btn-ghost text-xs" data-testid="detail-toggle-read">
                    {selected.read_at ? <><Circle className="w-3 h-3" /> Mark Unread</> : <><CircleDot className="w-3 h-3" /> Mark Read</>}
                  </button>
                  <button onClick={() => toggleArchive(selected.id, !selected.archived)} className="btn-ghost text-xs" data-testid="detail-toggle-archive">
                    {selected.archived ? <><ArchiveRestore className="w-3 h-3" /> Restore</> : <><Archive className="w-3 h-3" /> Archive</>}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-6">
                {tab === "quotes" ? (
                  <>
                    <DField label="Contact" value={selected.contact_name} icon={Building2} />
                    <DField label="Service" value={selected.service} icon={FileText} />
                    <DField label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#FF5722] hover:underline">{selected.email}</a>} icon={Mail} />
                    <DField label="Phone" value={<a href={`tel:${selected.phone}`} className="text-[#FF5722] hover:underline">{selected.phone}</a>} icon={Phone} />
                    <DField label="Industry" value={selected.industry} />
                    <DField label="Timeline" value={selected.timeline} />
                    <DField label="Quantity" value={selected.part_quantity} />
                    <DField label="Dimensions" value={selected.part_dimensions} />
                    <DField label="Substrate" value={selected.substrate} />
                    <DField label="Color / Finish" value={selected.color} />
                  </>
                ) : (
                  <>
                    <DField label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#FF5722] hover:underline">{selected.email}</a>} icon={Mail} />
                    <DField label="Phone" value={selected.phone && <a href={`tel:${selected.phone}`} className="text-[#FF5722] hover:underline">{selected.phone}</a>} icon={Phone} />
                    <DField label="Subject" value={selected.subject} />
                  </>
                )}
              </div>

              {selected.message && (
                <div className="pt-6 border-t border-white/10">
                  <div className="eyebrow mb-3">Message</div>
                  <p className="whitespace-pre-wrap text-zinc-200 leading-relaxed">{selected.message}</p>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap gap-3">
                <a href={`mailto:${selected.email}?subject=Re: Your inquiry with American Armor Coatings`} className="btn-primary" data-testid="detail-reply-email">
                  <Mail className="w-4 h-4" /> Reply by Email
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="btn-ghost">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, count, unread, testId }) => (
  <button onClick={onClick}
    className={`px-4 py-2 border font-display uppercase tracking-widest text-xs transition-colors flex items-center gap-2 ${active ? "bg-[#FF5722] border-[#FF5722] text-white" : "border-white/20 text-zinc-300 hover:border-white/40"}`}
    data-testid={testId}>
    <Icon className="w-4 h-4" /> {label}
    <span className={`px-1.5 py-0.5 text-[10px] ${active ? "bg-white/20" : "bg-white/10"}`}>{count}</span>
    {unread > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-[#FF5722] text-white">{unread}</span>}
  </button>
);

const DField = ({ label, value, icon: Icon }) => {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
};
